// dagAux — the "rest of the Claude DAG prototype", ported to operate on the
// REAL provenance graph (`@flexcompute/thread-graph`) instead of the prototype's
// hard-coded flight-dynamics fixture.
//
// ThreadV5DagExplorer.tsx ships the trimmed CORE (spine/cone/map + a basic trace
// lens). This module is the auxiliary half the integrator wires into it:
//
//   • SEARCH        — query → matching nodes → jump/select. `searchNodes`.
//   • HEALTH        — counts of failed / stale / running by status, plus a
//                     per-kind breakdown. `healthSummary`, `statusOf`.
//   • EXPORT        — download the current upstream cone as JSON
//                     (`exportConeJson` + `<DownloadConeButton>`) and copy a
//                     deep-link of the current sel/view/lens
//                     (`buildDeepLink` + `<CopyLinkButton>`).
//   • REBUILD       — turn the selected node's upstream cone into an ordered,
//                     reproducible step plan. `rebuildPlan`.
//   • TIME-TRAVEL   — reveal nodes built up to a time T over the graph's
//                     `created_at`. `timeTravel` + `<TimeTravelScrubber>`.
//   • TRACE DIR     — up / down / both toggle. `<TraceDirectionToggle>` +
//                     the `TraceDirection` type and `lineageForDirection`.
//   • RUN-GRID      — click a run → its conditions/outputs.
//                     `runConditions` + `<RunGridDrillIn>`.
//
// Everything graph-derived reads the real graph. Where the real data has no
// provenance meaning yet (a run's flow conditions, a node's health status, its
// build timestamp), we fall back to the prototype's DETERMINISTIC-from-id
// presentation so the VIEWS still light up — exactly the prototype's behaviour.
//
// Pure where possible: the graph helpers are plain functions with unit tests;
// the small components are thin presentational wrappers that consume them.
//
// Colours come from --v5-* / --fc-* design tokens (thread-v5.css) — no hex.

import { useMemo } from "react";

import {
  ancestors,
  buildIndex,
  descendants,
} from "@flexcompute/thread-graph";
import type { Graph, GraphIndex, GraphNode } from "@flexcompute/thread-graph";

// ─────────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────────

/** Up = sources, down = consumers, both = full lineage. */
export type TraceDirection = "up" | "down" | "both";

/** The cone lens (kept in sync with the explorer's DagLens superset). */
export type AuxLens = "trace" | "impact" | "diff" | "filter" | "health" | "versions";

/** A node's health, derived from real status when present, else deterministic. */
export type NodeStatus = "ok" | "failed" | "stale" | "running";

// ─────────────────────────────────────────────────────────────────────────────
// Raw-record readers — the canonical GraphNode keeps the source record on
// `data`. We read provenance fields off it (and any nested `value_json`),
// preferring real values and falling back to a deterministic hash of the id so
// the views still render against graphs that don't carry the field yet.
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_KEYS = ["status", "display_status", "run_status", "state", "rootItemStatus", "root_item_status"];
const CREATED_KEYS = ["created_at", "createdAt", "created", "updated_at", "updatedAt", "entry_date", "date"];

/** The raw source record for a node (the verbatim provenance row), incl. value_json. */
function recordOf(node: GraphNode | undefined): Record<string, unknown> {
  if (!node) return {};
  const data = (node.data ?? {}) as Record<string, unknown>;
  const vj = data.value_json;
  if (vj && typeof vj === "object" && !Array.isArray(vj)) {
    // value_json fields are shadowed by top-level fields of the same name.
    return { ...(vj as Record<string, unknown>), ...data };
  }
  return data;
}

function firstStringKey(rec: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return null;
}

/** A stable 0..(mod-1) hash of an id — the prototype's deterministic fallback. */
export function idHash(id: string, mod = 11): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h += id.charCodeAt(i);
  return ((h % mod) + mod) % mod;
}

/** Short deterministic 7-char git-style hash (prototype's `hashFor`). */
export function hashFor(id: string): string {
  let h = 0x811c9dc5;
  const s = `thread:${id}`;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `0000000${(h >>> 0).toString(16)}`.slice(-7);
}

// ─────────────────────────────────────────────────────────────────────────────
// HEALTH — node status + a failed/stale/running summary, by status and kind.
// ─────────────────────────────────────────────────────────────────────────────

const RUNNING_RE = /run+ing|in[_-]?progress|pending|queued|started/i;
const FAILED_RE = /fail|error|crash|diverged|abort|cancel/i;
const STALE_RE = /stale|outdated|dirty|superseded|behind/i;
const OK_RE = /ok|success|complete|done|finished|converged|ready|uploaded/i;

/**
 * A node's health. Reads a real status string off the provenance record when
 * present (mapped onto the four buckets); otherwise falls back to the
 * prototype's deterministic-from-id rule (hash%11 → 2:failed 5:stale 8:running).
 */
export function statusOf(node: GraphNode | undefined, id?: string): NodeStatus {
  const nid = node?.id ?? id ?? "";
  const raw = firstStringKey(recordOf(node), STATUS_KEYS);
  if (raw) {
    if (FAILED_RE.test(raw)) return "failed";
    if (STALE_RE.test(raw)) return "stale";
    if (RUNNING_RE.test(raw)) return "running";
    if (OK_RE.test(raw)) return "ok";
  }
  const h = idHash(nid);
  return h === 2 ? "failed" : h === 5 ? "stale" : h === 8 ? "running" : "ok";
}

export interface HealthSummary {
  total: number;
  ok: number;
  failed: number;
  stale: number;
  running: number;
  /** Per-kind status breakdown, kinds in first-seen order. */
  byKind: Array<{ kind: string; total: number; failed: number; stale: number; running: number; ok: number }>;
  /** True when nothing needs attention (no failed/stale/running). */
  healthy: boolean;
}

/** Roll the whole graph up into a health summary (used by the Health lens). */
export function healthSummary(graph: Graph): HealthSummary {
  const out: HealthSummary = {
    total: 0,
    ok: 0,
    failed: 0,
    stale: 0,
    running: 0,
    byKind: [],
    healthy: true,
  };
  const kindMap = new Map<string, { kind: string; total: number; failed: number; stale: number; running: number; ok: number }>();
  for (const node of graph.nodes) {
    const st = statusOf(node);
    out.total += 1;
    out[st] += 1;
    const kind = node.type || "node";
    const ke = kindMap.get(kind) ?? { kind, total: 0, failed: 0, stale: 0, running: 0, ok: 0 };
    ke.total += 1;
    ke[st] += 1;
    kindMap.set(kind, ke);
  }
  out.byKind = [...kindMap.values()];
  out.healthy = out.failed === 0 && out.stale === 0 && out.running === 0;
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// SEARCH — free-text query → ranked matching nodes, with a jump callback.
// Matches label, kind/type, id and the short hash (so a user can paste a hash).
// ─────────────────────────────────────────────────────────────────────────────

export interface SearchHit {
  id: string;
  label: string;
  kind: string;
  hash: string;
  /** Lower is better (0 = label prefix, then label substr, then field substr). */
  rank: number;
}

/**
 * Pure search over the graph. Returns up to `limit` hits, best-first. A node
 * matches if the query is a substring of its label / kind / id / hash.
 */
export function searchNodes(graph: Graph, query: string, limit = 8): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const hits: SearchHit[] = [];
  for (const node of graph.nodes) {
    const label = node.label ?? node.id;
    const kind = node.type ?? "node";
    const hash = hashFor(node.id);
    const lc = label.toLowerCase();
    let rank: number | null = null;
    if (lc.startsWith(q)) rank = 0;
    else if (lc.includes(q)) rank = 1;
    else if (kind.toLowerCase().includes(q)) rank = 2;
    else if (node.id.toLowerCase().includes(q)) rank = 3;
    else if (hash.includes(q)) rank = 4;
    if (rank !== null) hits.push({ id: node.id, label, kind, hash, rank });
  }
  hits.sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
  return hits.slice(0, limit);
}

// ─────────────────────────────────────────────────────────────────────────────
// TRACE DIRECTION — which way the cone reads. The lineage set for a direction.
// ─────────────────────────────────────────────────────────────────────────────

/** The lineage id-set for a root in a given direction (inclusive of the root). */
export function lineageForDirection(
  index: GraphIndex,
  rootId: string,
  direction: TraceDirection,
  depth = 32,
): Set<string> {
  const ids = new Set<string>([rootId]);
  if (!index.nodeById.has(rootId)) return ids;
  if (direction !== "down") for (const id of ancestors(index, rootId, depth).ids) ids.add(id);
  if (direction !== "up") for (const id of descendants(index, rootId, depth).ids) ids.add(id);
  return ids;
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT — deep-link of the current view + downloadable cone JSON.
// ─────────────────────────────────────────────────────────────────────────────

export interface DeepLinkState {
  selectedId: string;
  view?: string; // 'spine' | 'cone' | 'map'
  lens?: string; // AuxLens
  direction?: TraceDirection;
}

/**
 * The shareable URL-hash fragment for a view state — `#a=<sel>&v=cone&m=trace&dir=both`.
 * Mirrors the explorer's componentDidUpdate hash sync, generalised.
 */
export function buildDeepLinkHash(state: DeepLinkState): string {
  const p = new URLSearchParams();
  p.set("a", state.selectedId);
  if (state.view) p.set("v", state.view);
  if (state.lens) p.set("m", state.lens);
  if (state.direction) p.set("dir", state.direction);
  return `#${p.toString()}`;
}

/** Parse a hash fragment back into a partial deep-link state (deep-link restore). */
export function parseDeepLinkHash(hash: string): Partial<DeepLinkState> {
  const out: Partial<DeepLinkState> = {};
  try {
    const p = new URLSearchParams((hash || "").replace(/^#/, ""));
    const a = p.get("a");
    if (a) out.selectedId = a;
    const v = p.get("v");
    if (v) out.view = v;
    const m = p.get("m");
    if (m) out.lens = m;
    const dir = p.get("dir");
    if (dir === "up" || dir === "down" || dir === "both") out.direction = dir;
  } catch {
    // malformed hash → empty patch
  }
  return out;
}

/** A full absolute deep-link for the current view (origin + path + hash). */
export function buildDeepLink(state: DeepLinkState, base?: { origin: string; pathname: string }): string {
  const loc =
    base ??
    (typeof location !== "undefined"
      ? { origin: location.origin, pathname: location.pathname }
      : { origin: "", pathname: "" });
  return `${loc.origin}${loc.pathname}${buildDeepLinkHash(state)}`;
}

export interface ConeExport {
  root: string;
  rootHash: string;
  generated: string;
  direction: TraceDirection;
  nodes: Array<{ id: string; label: string; kind: string; hash: string }>;
  edges: Array<{ from: string; to: string }>;
}

/**
 * Serialise the current cone (the lineage in `direction` from `rootId`) as a
 * self-contained JSON doc — the artifact a user attaches to a PR / report.
 */
export function exportConeJson(
  graph: Graph,
  index: GraphIndex,
  rootId: string,
  direction: TraceDirection = "up",
): ConeExport {
  const ids = lineageForDirection(index, rootId, direction);
  const nodes = graph.nodes
    .filter((n) => ids.has(n.id))
    .map((n) => ({ id: n.id, label: n.label ?? n.id, kind: n.type ?? "node", hash: hashFor(n.id) }));
  const edges = graph.edges
    .filter((e) => ids.has(e.source) && ids.has(e.target))
    .map((e) => ({ from: e.source, to: e.target }));
  return {
    root: rootId,
    rootHash: hashFor(rootId),
    generated: new Date().toISOString().slice(0, 10),
    direction,
    nodes,
    edges,
  };
}

/** Trigger a browser download of the cone JSON. Side-effecting; no-op if no DOM. */
export function downloadConeJson(doc: ConeExport): void {
  if (typeof document === "undefined") return;
  try {
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cone-${doc.rootHash}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } catch {
    // download unavailable (SSR / sandbox) — silently skip
  }
}

/** Copy text to the clipboard, resolving to whether it succeeded. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// REBUILD-FROM-HERE — the upstream cone as an ordered reproducible step plan.
// ─────────────────────────────────────────────────────────────────────────────

/** The build verb for a kind — what re-running this step actually does. */
const VERB_BY_KIND: Array<[RegExp, string]> = [
  [/geometr|airfoil|cad|step|param/, "import"],
  [/surface_mesh/, "mesh"],
  [/volume_mesh|mesh/, "mesh"],
  [/run|case|solve|simulation|sample/, "solve"],
  [/matrix|derivative|assemble/, "assemble"],
  [/quantity|dataframe|value|coefficient|db|database|data/, "build"],
  [/plot|figure|chart|report|view|envelope|artifact/, "render"],
];

function verbFor(kind: string): string {
  const k = (kind || "").toLowerCase();
  for (const [re, verb] of VERB_BY_KIND) if (re.test(k)) return verb;
  return "run";
}

export interface RebuildStep {
  i: number;
  badge: string; // zero-padded ordinal, e.g. "03"
  id: string;
  label: string;
  kind: string;
  hash: string;
  verb: string;
  /** Topological depth from the deepest source (0 = a source with no inputs). */
  depth: number;
}

export interface RebuildPlan {
  root: string;
  rootLabel: string;
  hash: string;
  stepCount: number;
  runCount: number;
  steps: RebuildStep[];
}

/**
 * Turn the selected node's UPSTREAM cone into an ordered build plan: sources
 * first, the selection last, ordered by topological depth (longest path from a
 * source) so every step's inputs come before it. Deterministic tie-break by
 * label keeps the plan stable.
 */
export function rebuildPlan(index: GraphIndex, rootId: string): RebuildPlan {
  const ids = ancestors(index, rootId, 32).ids; // inclusive of root
  const inCone = [...ids];

  // Longest-path depth within the cone (a step's depth > all its in-cone inputs).
  const depthOf = new Map<string, number>();
  const visiting = new Set<string>();
  const computeDepth = (id: string): number => {
    const cached = depthOf.get(id);
    if (cached !== undefined) return cached;
    if (visiting.has(id)) return 0; // cycle guard (iteration loops are real here)
    visiting.add(id);
    let d = 0;
    for (const p of index.predecessors.get(id) ?? []) {
      if (ids.has(p)) d = Math.max(d, computeDepth(p) + 1);
    }
    visiting.delete(id);
    depthOf.set(id, d);
    return d;
  };
  for (const id of inCone) computeDepth(id);

  const ordered = inCone
    .map((id) => {
      const n = index.nodeById.get(id)!;
      return { id, node: n, depth: depthOf.get(id) ?? 0 };
    })
    .sort((a, b) => a.depth - b.depth || (a.node.label ?? a.id).localeCompare(b.node.label ?? b.id));

  let runCount = 0;
  const steps: RebuildStep[] = ordered.map((s, i) => {
    const kind = s.node.type ?? "node";
    const verb = verbFor(kind);
    if (verb === "solve") runCount += 1;
    return {
      i: i + 1,
      badge: String(i + 1).padStart(2, "0"),
      id: s.id,
      label: s.node.label ?? s.id,
      kind,
      hash: hashFor(s.id),
      verb,
      depth: s.depth,
    };
  });

  const root = index.nodeById.get(rootId);
  return {
    root: rootId,
    rootLabel: root?.label ?? rootId,
    hash: hashFor(rootId),
    stepCount: steps.length,
    runCount,
    steps,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TIME-TRAVEL — reveal nodes built up to a time T over the graph's created_at.
// Real timestamps when present; else a deterministic build-day from topo depth.
// ─────────────────────────────────────────────────────────────────────────────

/** Parse a node's build time to an epoch-ms, or null when it has no timestamp. */
export function builtAtMs(node: GraphNode | undefined): number | null {
  const raw = firstStringKey(recordOf(node), CREATED_KEYS);
  if (!raw) return null;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : null;
}

export interface TimeTravelModel {
  /** Distinct ascending build instants (ms), the scrubber's stops. */
  stops: number[];
  /** Whether real timestamps were found (vs. the synthetic topo-day fallback). */
  real: boolean;
  /** built[id] = the stop index at which the node appears (0-based). */
  builtIndex: Map<string, number>;
  /** Label for a stop (a date when real, else "day N"). */
  labelAt: (stopIdx: number) => string;
  /** The ids present at-or-before a given stop index. */
  revealedAt: (stopIdx: number) => Set<string>;
  /** Total number of stops (≥ 1). */
  count: number;
}

/**
 * Build the time-travel model. If the nodes carry real `created_at`, the stops
 * are the distinct days and a node appears on its day; otherwise we synthesise a
 * monotone "build day" from each node's topological depth (sources first) so the
 * scrubber still plays the build back — matching the prototype's `builtAt`.
 */
export function timeTravel(graph: Graph, index: GraphIndex): TimeTravelModel {
  const realMs = new Map<string, number>();
  let anyReal = false;
  for (const n of graph.nodes) {
    const ms = builtAtMs(n);
    if (ms !== null) {
      realMs.set(n.id, ms);
      anyReal = true;
    }
  }

  // Per-node build "instant": real day-rounded epoch, or synthetic topo depth.
  const dayMs = 86_400_000;
  const depthOf = synthDepth(graph, index);
  const instant = new Map<string, number>();
  for (const n of graph.nodes) {
    if (anyReal) {
      const ms = realMs.get(n.id);
      // Nodes missing a real ts fall back to their topo depth mapped past the
      // real range so they still appear last; keep it deterministic.
      instant.set(n.id, ms !== undefined ? Math.floor(ms / dayMs) * dayMs : Number.MAX_SAFE_INTEGER - (1000 - (depthOf.get(n.id) ?? 0)));
    } else {
      instant.set(n.id, depthOf.get(n.id) ?? 0);
    }
  }

  const stops = [...new Set(instant.values())].sort((a, b) => a - b);
  const stopIndex = new Map<number, number>();
  stops.forEach((s, i) => stopIndex.set(s, i));

  const builtIndex = new Map<string, number>();
  for (const n of graph.nodes) builtIndex.set(n.id, stopIndex.get(instant.get(n.id) ?? 0) ?? 0);

  const labelAt = (i: number): string => {
    const s = stops[i];
    if (s === undefined) return "—";
    if (anyReal && s < Number.MAX_SAFE_INTEGER - 2000) {
      return new Date(s).toISOString().slice(0, 10);
    }
    return `day ${anyReal ? i : s}`;
  };

  const revealedAt = (i: number): Set<string> => {
    const out = new Set<string>();
    for (const n of graph.nodes) if ((builtIndex.get(n.id) ?? 0) <= i) out.add(n.id);
    return out;
  };

  return {
    stops,
    real: anyReal,
    builtIndex,
    labelAt,
    revealedAt,
    count: Math.max(1, stops.length),
  };
}

/** Synthetic monotone build depth: longest path from a source (cycle-safe). */
function synthDepth(graph: Graph, index: GraphIndex): Map<string, number> {
  const depthOf = new Map<string, number>();
  const visiting = new Set<string>();
  const compute = (id: string): number => {
    const cached = depthOf.get(id);
    if (cached !== undefined) return cached;
    if (visiting.has(id)) return 0;
    visiting.add(id);
    let d = 0;
    for (const p of index.predecessors.get(id) ?? []) d = Math.max(d, compute(p) + 1);
    visiting.delete(id);
    depthOf.set(id, d);
    return d;
  };
  for (const n of graph.nodes) compute(n.id);
  return depthOf;
}

// ─────────────────────────────────────────────────────────────────────────────
// RUN-GRID — drill into one run: its conditions (inputs) + outputs.
// Real values come off the provenance record's value_json; missing fields fall
// back to the prototype's deterministic flow conditions so a run always reads.
// ─────────────────────────────────────────────────────────────────────────────

const COND_KEYS: Record<string, string[]> = {
  mach: ["mach", "Mach", "M", "mach_number"],
  alpha: ["alpha", "Alpha", "aoa", "angle_of_attack", "AoA"],
  beta: ["beta", "Beta", "sideslip"],
  re: ["reynolds", "Re", "reynolds_number", "re"],
};
const OUT_KEYS: Record<string, string[]> = {
  cl: ["CL", "cl", "C_L", "lift_coefficient"],
  cd: ["CD", "cd", "C_D", "drag_coefficient"],
  cm: ["CM", "cm", "C_m", "moment_coefficient"],
};

export interface RunDetail {
  id: string;
  label: string;
  hash: string;
  status: NodeStatus;
  /** Whether any field below came from real data (vs. the deterministic fallback). */
  synthetic: boolean;
  conditions: { mach: string; alpha: string; beta: string; re: string };
  outputs: { cl: string; cd: string; cm: string };
}

function pickNumber(rec: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
}

/**
 * Deterministic flow conditions + coefficients for a run, indexed by a stable
 * integer derived from its id (the prototype's `condsFor`). Used to backfill any
 * field the real record doesn't carry, so a drilled-in run always shows detail.
 */
export function syntheticConditions(seedId: string): RunDetail["conditions"] & RunDetail["outputs"] {
  const i = idHash(seedId, 997) + 1;
  const mach = 0.3 + ((i * 37) % 61) / 100;
  const alpha = -4 + ((i * 53) % 170) / 10;
  const beta = -6 + ((i * 17) % 120) / 10;
  const re = 2.0 + ((i * 7) % 80) / 10;
  const cl = 0.12 + alpha * 0.092;
  const cd = 0.012 + Math.abs(alpha) * 0.0009 + (mach > 0.78 ? 0.011 : 0);
  const cm = -0.02 - alpha * 0.004;
  return {
    mach: mach.toFixed(2),
    alpha: alpha.toFixed(1),
    beta: beta.toFixed(1),
    re: re.toFixed(1),
    cl: cl.toFixed(3),
    cd: cd.toFixed(4),
    cm: cm.toFixed(4),
  };
}

/** The full drill-in detail for a run node (real where present, synthetic fill). */
export function runConditions(node: GraphNode | undefined, id?: string): RunDetail {
  const nid = node?.id ?? id ?? "";
  const rec = recordOf(node);
  const synth = syntheticConditions(nid);
  const num = (keys: string[]): number | null => pickNumber(rec, keys);
  const mach = num(COND_KEYS.mach!);
  const alpha = num(COND_KEYS.alpha!);
  const beta = num(COND_KEYS.beta!);
  const re = num(COND_KEYS.re!);
  const cl = num(OUT_KEYS.cl!);
  const cd = num(OUT_KEYS.cd!);
  const cm = num(OUT_KEYS.cm!);
  const synthetic =
    mach === null && alpha === null && beta === null && re === null && cl === null && cd === null && cm === null;
  return {
    id: nid,
    label: node?.label ?? nid,
    hash: hashFor(nid),
    status: statusOf(node, nid),
    synthetic,
    conditions: {
      mach: mach !== null ? mach.toFixed(2) : synth.mach,
      alpha: alpha !== null ? alpha.toFixed(1) : synth.alpha,
      beta: beta !== null ? beta.toFixed(1) : synth.beta,
      re: re !== null ? re.toFixed(1) : synth.re,
    },
    outputs: {
      cl: cl !== null ? cl.toFixed(3) : synth.cl,
      cd: cd !== null ? cd.toFixed(4) : synth.cd,
      cm: cm !== null ? cm.toFixed(4) : synth.cm,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Components — thin presentational wrappers over the helpers above.
// All colours via --v5-* / --fc-* tokens (className-driven; no inline hex).
// ─────────────────────────────────────────────────────────────────────────────

/** SEARCH box + jump-to results list. Controlled query; pure render off props. */
export function DagSearchBox({
  graph,
  query,
  onQueryChange,
  onJump,
  limit = 8,
}: {
  graph: Graph;
  query: string;
  onQueryChange: (q: string) => void;
  onJump: (id: string) => void;
  limit?: number;
}) {
  const hits = useMemo(() => searchNodes(graph, query, limit), [graph, query, limit]);
  const has = query.trim().length > 0;
  return (
    <div className="v5-dag-search" data-testid="v5-dag-search">
      <div className="v5-dag-search-field">
        <input
          type="search"
          className="v5-dag-search-input"
          placeholder="Search lineage…"
          aria-label="Search lineage"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
        {has ? (
          <button
            type="button"
            className="v5-text-button v5-dag-search-clear"
            aria-label="Clear search"
            onClick={() => onQueryChange("")}
          >
            ×
          </button>
        ) : null}
      </div>
      {has ? (
        <ul className="v5-dag-search-results" role="listbox" aria-label="Search results">
          {hits.length === 0 ? (
            <li className="v5-dag-search-empty v5-muted">no matches</li>
          ) : (
            hits.map((h) => (
              <li key={h.id}>
                <button
                  type="button"
                  className="v5-dag-search-hit"
                  data-node-id={h.id}
                  role="option"
                  aria-selected="false"
                  onClick={() => {
                    onJump(h.id);
                    onQueryChange("");
                  }}
                >
                  <span className="v5-dag-search-hit-label">{h.label}</span>
                  <span className="v5-dag-search-hit-kind v5-muted">{h.kind}</span>
                  <span className="v5-dag-search-hit-hash v5-mono v5-muted">{h.hash}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/** HEALTH summary readout — the four buckets + a per-kind breakdown. */
export function HealthPanel({ graph }: { graph: Graph }) {
  const h = useMemo(() => healthSummary(graph), [graph]);
  const chips: Array<{ key: NodeStatus; label: string; n: number }> = [
    { key: "failed", label: "failed", n: h.failed },
    { key: "stale", label: "stale", n: h.stale },
    { key: "running", label: "running", n: h.running },
    { key: "ok", label: "ok", n: h.ok },
  ];
  return (
    <div className="v5-dag-health" data-testid="v5-dag-health" data-healthy={h.healthy ? "true" : "false"}>
      <div className="v5-dag-health-row">
        {chips.map((c) => (
          <span
            key={c.key}
            className={`v5-dag-health-chip is-${c.key}`}
            data-status={c.key}
            data-count={c.n}
          >
            <span className="v5-dag-health-chip-n">{c.n}</span>
            <span className="v5-dag-health-chip-label">{c.label}</span>
          </span>
        ))}
      </div>
      {h.healthy ? (
        <p className="v5-dag-health-note v5-muted">all {h.total} artifacts healthy</p>
      ) : (
        <ul className="v5-dag-health-kinds">
          {h.byKind
            .filter((k) => k.failed + k.stale + k.running > 0)
            .map((k) => (
              <li key={k.kind} className="v5-dag-health-kind">
                <span className="v5-dag-health-kind-name">{k.kind}</span>
                {k.failed ? <span className="is-failed">{k.failed} failed</span> : null}
                {k.stale ? <span className="is-stale">{k.stale} stale</span> : null}
                {k.running ? <span className="is-running">{k.running} running</span> : null}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

/** TRACE DIRECTION up / down / both toggle. */
export function TraceDirectionToggle({
  direction,
  onChange,
}: {
  direction: TraceDirection;
  onChange: (d: TraceDirection) => void;
}) {
  const opts: Array<{ key: TraceDirection; label: string; title: string }> = [
    { key: "both", label: "↕ both", title: "Sources and consumers" },
    { key: "up", label: "↑ up", title: "Upstream sources only" },
    { key: "down", label: "↓ down", title: "Downstream consumers only" },
  ];
  return (
    <div className="v5-dag-trace-dir" role="group" aria-label="Trace direction" data-testid="v5-dag-trace-dir">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          className={`v5-dag-trace-dir-btn${direction === o.key ? " is-active" : ""}`}
          aria-pressed={direction === o.key}
          title={o.title}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** EXPORT: copy a deep-link of the current view. */
export function CopyLinkButton({
  state,
  copied,
  onCopied,
}: {
  state: DeepLinkState;
  copied?: boolean;
  onCopied?: (link: string) => void;
}) {
  return (
    <button
      type="button"
      className={`v5-text-button v5-dag-copy-link${copied ? " is-done" : ""}`}
      data-testid="v5-dag-copy-link"
      title="Copy a shareable link to this exact view"
      onClick={async () => {
        const link = buildDeepLink(state);
        await copyToClipboard(link);
        onCopied?.(link);
      }}
    >
      {copied ? "✓ link copied" : "⎘ copy link"}
    </button>
  );
}

/** EXPORT: download the current cone as JSON. */
export function DownloadConeButton({
  graph,
  index,
  rootId,
  direction = "up",
  done,
  onDone,
}: {
  graph: Graph;
  index: GraphIndex;
  rootId: string;
  direction?: TraceDirection;
  done?: boolean;
  onDone?: (doc: ConeExport) => void;
}) {
  return (
    <button
      type="button"
      className={`v5-text-button v5-dag-export-cone${done ? " is-done" : ""}`}
      data-testid="v5-dag-export-cone"
      title="Download this lineage cone as JSON (for a PR / report)"
      onClick={() => {
        const doc = exportConeJson(graph, index, rootId, direction);
        downloadConeJson(doc);
        onDone?.(doc);
      }}
    >
      {done ? "✓ cone.json" : "↓ export cone"}
    </button>
  );
}

/** REBUILD-FROM-HERE: the ordered reproducible step plan for the selection. */
export function RebuildPlanPanel({
  index,
  rootId,
}: {
  index: GraphIndex;
  rootId: string;
}) {
  const plan = useMemo(() => rebuildPlan(index, rootId), [index, rootId]);
  return (
    <div className="v5-dag-rebuild" data-testid="v5-dag-rebuild" data-root={rootId}>
      <div className="v5-dag-rebuild-head">
        <span className="v5-dag-rebuild-title">Rebuild {plan.rootLabel}</span>
        <span className="v5-dag-rebuild-meta v5-muted">
          {plan.stepCount} step{plan.stepCount === 1 ? "" : "s"}
          {plan.runCount ? ` · ${plan.runCount} solve${plan.runCount === 1 ? "" : "s"}` : ""}
        </span>
      </div>
      <ol className="v5-dag-rebuild-steps">
        {plan.steps.map((s) => (
          <li key={s.id} className="v5-dag-rebuild-step" data-node-id={s.id} data-verb={s.verb}>
            <span className="v5-dag-rebuild-step-badge v5-mono">{s.badge}</span>
            <span className="v5-dag-rebuild-step-verb">{s.verb}</span>
            <span className="v5-dag-rebuild-step-label">{s.label}</span>
            <span className="v5-dag-rebuild-step-kind v5-muted">{s.kind}</span>
            <span className="v5-dag-rebuild-step-hash v5-mono v5-muted">{s.hash}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** TIME-TRAVEL scrubber — slide T over the build timeline; emits revealed-ids. */
export function TimeTravelScrubber({
  graph,
  index,
  value,
  onChange,
}: {
  graph: Graph;
  index: GraphIndex;
  /** Current stop index (0 = first build instant; model.count-1 = present). */
  value: number;
  onChange: (stopIdx: number, revealed: Set<string>) => void;
}) {
  const model = useMemo(() => timeTravel(graph, index), [graph, index]);
  const max = model.count - 1;
  const idx = Math.max(0, Math.min(max, value));
  const revealedCount = useMemo(() => model.revealedAt(idx).size, [model, idx]);
  const isPresent = idx >= max;
  const emit = (i: number) => onChange(i, model.revealedAt(i));
  return (
    <div
      className="v5-dag-timetravel"
      data-testid="v5-dag-timetravel"
      data-present={isPresent ? "true" : "false"}
    >
      <div className="v5-dag-timetravel-row">
        <button
          type="button"
          className="v5-text-button v5-dag-tt-step"
          aria-label="Step back"
          disabled={idx <= 0}
          onClick={() => emit(Math.max(0, idx - 1))}
        >
          ◀
        </button>
        <input
          type="range"
          className="v5-dag-timetravel-range"
          aria-label="Build timeline"
          min={0}
          max={max}
          step={1}
          value={idx}
          onChange={(e) => emit(parseInt(e.target.value, 10))}
        />
        <button
          type="button"
          className="v5-text-button v5-dag-tt-step"
          aria-label="Step forward"
          disabled={idx >= max}
          onClick={() => emit(Math.min(max, idx + 1))}
        >
          ▶
        </button>
        {!isPresent ? (
          <button
            type="button"
            className="v5-text-button v5-dag-tt-now"
            onClick={() => emit(max)}
          >
            now
          </button>
        ) : null}
      </div>
      <div className="v5-dag-timetravel-caption v5-muted">
        <span className="v5-dag-tt-label">{model.labelAt(idx)}</span>
        <span className="v5-dag-tt-count">
          {revealedCount} / {graph.nodes.length} built
        </span>
      </div>
    </div>
  );
}

/** RUN-GRID drill-in — click a run cell → its conditions + outputs. */
export function RunGridDrillIn({
  graph,
  index,
  runIds,
  selectedRunId,
  onSelectRun,
  maxCells = 47,
}: {
  graph: Graph;
  index: GraphIndex;
  /** Explicit run ids; if omitted, every run-kind node in the graph is used. */
  runIds?: string[];
  selectedRunId: string | null;
  onSelectRun: (id: string | null) => void;
  maxCells?: number;
}) {
  const allRuns = useMemo(() => {
    if (runIds) return runIds;
    return graph.nodes
      .filter((n) => /run|case|solve|simulation|sample/i.test(n.type ?? ""))
      .map((n) => n.id);
  }, [graph, runIds]);

  const cells = allRuns.slice(0, maxCells);
  const overflow = allRuns.length - cells.length;
  const detail = useMemo(
    () => (selectedRunId ? runConditions(index.nodeById.get(selectedRunId), selectedRunId) : null),
    [index, selectedRunId],
  );

  return (
    <div className="v5-dag-rungrid" data-testid="v5-dag-rungrid" data-run-count={allRuns.length}>
      <div className="v5-dag-rungrid-cells" role="list" aria-label="Run grid">
        {cells.map((id) => {
          const node = index.nodeById.get(id);
          const st = statusOf(node, id);
          const active = id === selectedRunId;
          return (
            <button
              key={id}
              type="button"
              className={`v5-dag-rungrid-cell is-${st}${active ? " is-active" : ""}`}
              role="listitem"
              data-node-id={id}
              data-status={st}
              aria-pressed={active}
              title={node?.label ?? id}
              onClick={() => onSelectRun(active ? null : id)}
            >
              <span className="v5-dag-rungrid-cell-hash v5-mono">{hashFor(id).slice(0, 4)}</span>
            </button>
          );
        })}
        {overflow > 0 ? (
          <span className="v5-dag-rungrid-more v5-muted" data-testid="v5-dag-rungrid-more">
            +{overflow.toLocaleString()} more
          </span>
        ) : null}
      </div>

      {detail ? (
        <div className="v5-dag-rungrid-detail" data-testid="v5-dag-run-detail" data-node-id={detail.id}>
          <div className="v5-dag-rungrid-detail-head">
            <span className="v5-dag-rungrid-detail-label">{detail.label}</span>
            <span className="v5-dag-rungrid-detail-hash v5-mono v5-muted">{detail.hash}</span>
            <button
              type="button"
              className="v5-text-button v5-dag-rungrid-detail-close"
              aria-label="Close run detail"
              onClick={() => onSelectRun(null)}
            >
              ×
            </button>
          </div>
          <dl className="v5-dag-rungrid-detail-grid">
            <div><dt>Mach</dt><dd className="v5-mono">{detail.conditions.mach}</dd></div>
            <div><dt>α</dt><dd className="v5-mono">{detail.conditions.alpha}°</dd></div>
            <div><dt>β</dt><dd className="v5-mono">{detail.conditions.beta}°</dd></div>
            <div><dt>Re×10⁶</dt><dd className="v5-mono">{detail.conditions.re}</dd></div>
            <div><dt>C_L</dt><dd className="v5-mono">{detail.outputs.cl}</dd></div>
            <div><dt>C_D</dt><dd className="v5-mono">{detail.outputs.cd}</dd></div>
            <div><dt>C_m</dt><dd className="v5-mono">{detail.outputs.cm}</dd></div>
          </dl>
          {detail.synthetic ? (
            <p className="v5-dag-rungrid-detail-note v5-muted">illustrative conditions (no measured values on this run)</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience: build an index from a canonical graph (re-export for callers that
// only import this module). Keeps the explorer's wiring terse.
// ─────────────────────────────────────────────────────────────────────────────

export { buildIndex };
